/**
 * Volley tracer — the visible half of the pilum throw's release point.
 *
 * When a formation's throw clip crosses its timed "release" event
 * (render/anim.ts), main.ts calls volley.spawn() with the formation
 * anchor and facing. Darts arc forward ballistically and fade — one
 * InstancedMesh, one draw call, no sim state. This is presentation only:
 * damage still comes from sim/combat.ts.
 */
import * as THREE from "three";

interface Dart {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  t: number;
  life: number;
}

const MAX_DARTS = 128;
const GRAVITY = 18;
const DART_RANGE = 26;

export class VolleyField {
  private mesh: THREE.InstancedMesh;
  private darts: Dart[] = [];
  private dummy = new THREE.Object3D();
  private groundY: (x: number, z: number) => number;

  /** Live dart count (smoke-test probe). */
  get active(): number {
    return this.darts.length;
  }

  constructor(scene: THREE.Scene, groundY: (x: number, z: number) => number) {
    this.groundY = groundY;
    // A slim dart: shaft + head suggestion. Reads at RTS distance.
    const geo = new THREE.CylinderGeometry(0.03, 0.05, 1.6, 5);
    geo.translate(0, 0.8, 0);
    geo.rotateX(Math.PI / 2); // point along +z for lookAt-style orientation
    const mat = new THREE.MeshBasicMaterial({ color: 0x6b5a43 });
    this.mesh = new THREE.InstancedMesh(geo, mat, MAX_DARTS);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
  }

  /**
   * Loose a volley of `count` darts from (x, z) along `heading`.
   * Called on the throw clip's release event.
   */
  spawn(x: number, z: number, heading: number, count = 8): void {
    const dx = Math.sin(heading);
    const dz = Math.cos(heading);
    for (let i = 0; i < count; i++) {
      if (this.darts.length >= MAX_DARTS) this.darts.shift();
      const spread = (Math.random() - 0.5) * 4;
      const px = x + (Math.random() - 0.5) * 6 - dz * spread * 0.2;
      const pz = z + (Math.random() - 0.5) * 6 + dx * spread * 0.2;
      const range = DART_RANGE * (0.85 + Math.random() * 0.3);
      const flight = 0.9;
      this.darts.push({
        x: px,
        y: this.groundY(px, pz) + 1.6,
        z: pz,
        vx: dx * (range / flight),
        vy: 3.2,
        vz: dz * (range / flight),
        t: 0,
        life: flight + 0.5,
      });
    }
  }

  update(dt: number): void {
    const d = this.dummy;
    let n = 0;
    for (let i = this.darts.length - 1; i >= 0; i--) {
      const p = this.darts[i];
      p.t += dt;
      if (p.t >= p.life) {
        this.darts.splice(i, 1);
        continue;
      }
      p.vy -= GRAVITY * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      const gy = this.groundY(p.x, p.z);
      if (p.y < gy) {
        p.y = gy;
        p.vx = 0;
        p.vy = 0;
        p.vz = 0;
      }
      d.position.set(p.x, p.y, p.z);
      // Orient along velocity while flying; leave stuck darts as they land.
      if (p.vy !== 0 || p.vx !== 0 || p.vz !== 0) {
        d.lookAt(p.x + p.vx, p.y + p.vy, p.z + p.vz);
      }
      d.updateMatrix();
      this.mesh.setMatrixAt(n++, d.matrix);
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
